import {
  ContributionType,
  CONTRIBUTION_TYPES,
} from "@/data/contribution-types";
import {
  getDefaultSortDirection,
  SortOption,
  type SortDirection,
} from "@/util/contributions";
import { trimSearchQuery } from "@/util/search-query";

export type ContributionFilters = {
  query: string;
  sort: SortOption;
  sortDirection: SortDirection;
  typeFilter: ContributionType | null;
  techFilter: string | null;
};

/**
 * Parses and validates the contribution filter deeplink parameters
 * (?q, ?sort, ?dir, ?type, ?tech). Runs on the client so the route stays
 * statically rendered; reading searchParams on the server would force
 * per-request rendering of the whole page.
 */
export function parseContributionFilters(
  searchParams: URLSearchParams,
  uniqueTechnologies: readonly string[],
): ContributionFilters {
  const sortValue = searchParams.get("sort") ?? "";
  const typeValue = searchParams.get("type") ?? "";
  const techValue = searchParams.get("tech") ?? "";

  const sort = Object.values(SortOption).includes(sortValue as SortOption)
    ? (sortValue as SortOption)
    : SortOption.Stars;
  const directionValue = searchParams.get("dir");

  return {
    query: trimSearchQuery(searchParams.get("q") ?? ""),
    sort,
    sortDirection:
      directionValue === "asc" || directionValue === "desc"
        ? directionValue
        : getDefaultSortDirection(sort),
    typeFilter: CONTRIBUTION_TYPES.includes(typeValue as ContributionType)
      ? (typeValue as ContributionType)
      : null,
    techFilter: uniqueTechnologies.includes(techValue) ? techValue : null,
  };
}

export function serializeContributionFilters(
  filters: ContributionFilters,
  currentSearchParams = new URLSearchParams(),
): URLSearchParams {
  const params = new URLSearchParams(currentSearchParams);

  if (filters.query) params.set("q", filters.query);
  else params.delete("q");

  if (filters.sort !== SortOption.Stars) params.set("sort", filters.sort);
  else params.delete("sort");

  if (filters.sortDirection !== getDefaultSortDirection(filters.sort)) {
    params.set("dir", filters.sortDirection);
  } else {
    params.delete("dir");
  }

  if (filters.typeFilter) params.set("type", filters.typeFilter);
  else params.delete("type");

  if (filters.techFilter) params.set("tech", filters.techFilter);
  else params.delete("tech");

  return params;
}
